/**
 * TIKTOK CAMPAIGN TREE — the "Overview" tab on /tiktok/campaigns.
 *
 * Campaign → ad group → ad, one card per campaign expanding inline, the same
 * visual language as the Google Ads workspace tree. All three lists load up
 * front (they are the CRM's own synced tables, cheap to read) so every row can
 * carry its child counts without a click; per-level performance for the chosen
 * window rides on the rows, keyed by TikTok id.
 *
 * Editing and deleting TikTok entities have no CRM route yet — the workspace
 * only resumes local drafts and the service exposes no delete — so those
 * controls render disabled with a tooltip rather than vanish, mirroring the
 * Google tree.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import clsx from 'clsx';
import toast from 'react-hot-toast';
import {
  ChevronDown, ChevronRight, Film, ImageIcon, Layers, LayoutGrid, List, Megaphone, Music2,
  Play, Plus, RefreshCw, UserCircle2, XCircle, FileText, Search,
} from 'lucide-react';
import { Button } from '@/components/Button';
import { Card } from '@/components/Card';
import { Pill, Spinner, Switch, alreadyToasted, errMsg, pretty } from '@/components/google-ads/youtube/youtubeShared';
import { reportWindow } from './reportingUtils';
import { deliveryStatus } from './deliveryStatus';
import { useTiktokAccountCurrency } from '@/hooks/useTiktokAccountCurrency';
import {
  tiktokService, type TiktokAd, type TiktokAdGroup, type TiktokCampaign, type TiktokInsightsRow,
} from '@/services/tiktok.service';
import { Select } from '@/components/Select';

// ── helpers ──────────────────────────────────────────────────────────────

type Identity = Awaited<ReturnType<typeof tiktokService.listIdentities>>[number];
type RangeDays = 7 | 30 | 90;
const fmtNum = (n?: number | null) => (n == null ? '—' : Number(n).toLocaleString());
const fmtMoney = (n?: number | null, cur?: string | null) =>
  n == null ? '—' : `${cur ? `${cur} ` : ''}${Number(n).toLocaleString(undefined, { maximumFractionDigits: 2 })}`;

const fmtBudget = (mode?: string, budget?: number | null, currency?: string | null) => {
  if (!mode || mode === 'BUDGET_MODE_INFINITE') return 'No budget cap';
  const amount = budget != null ? fmtMoney(budget, currency) : '—';
  return mode === 'BUDGET_MODE_DAY' ? `${amount} / day` : `${amount} lifetime`;
};

const plural = (n: number, word: string) => `${n} ${word}${n === 1 ? '' : 's'}`;

/** Black TikTok tile — the brand mark, like the YouTube tree's red box. */
const BrandTile = ({ size = 'md' }: { size?: 'sm' | 'md' | 'lg' }) => (
  <span className={clsx(
    'flex shrink-0 items-center justify-center rounded-full bg-black text-white dark:bg-white dark:text-black',
    size === 'lg' ? 'h-12 w-12' : size === 'md' ? 'h-10 w-10' : 'h-7 w-7',
  )}>
    <Music2 size={size === 'lg' ? 22 : size === 'md' ? 17 : 13} />
  </span>
);

/** Compact per-row performance line. Quiet when nothing was spent. */
function MetricsLine({ m, currency }: { m?: TiktokInsightsRow; currency?: string | null }) {
  if (!m) return <span className="text-2xs text-text-muted">No reported metrics</span>;
  if (!m.spend && !m.impressions && !m.clicks) {
    return <span className="text-2xs text-text-muted/70">No spend in this period</span>;
  }
  const ctr = m.impressions > 0 ? ((m.clicks / m.impressions) * 100).toFixed(2) : '0.00';
  return (
    <span className="flex flex-wrap items-center gap-x-2 text-2xs text-text-muted">
      <span><span className="font-medium text-text-secondary">{fmtMoney(m.spend, currency)}</span> spent</span>
      <span aria-hidden>·</span>
      <span><span className="font-medium text-text-secondary">{fmtNum(m.impressions)}</span> impr.</span>
      <span aria-hidden>·</span>
      <span><span className="font-medium text-text-secondary">{fmtNum(m.clicks)}</span> clicks</span>
      <span aria-hidden>·</span>
      <span>CTR <span className="font-medium text-text-secondary">{ctr}%</span></span>
    </span>
  );
}

const SkeletonRows = ({ count = 3, height = 'h-10', className }: { count?: number; height?: string; className?: string }) => (
  <div className={clsx('space-y-2', className)}>
    {Array.from({ length: count }, (_, i) => <div key={i} className={clsx('skeleton rounded-xl', height)} />)}
  </div>
);

// ── ads (third level) ────────────────────────────────────────────────────

/** Image / video cover for the ad, or a format-appropriate placeholder while it resolves. */
function AdThumb({ ad, url }: { ad: TiktokAd; url?: string }) {
  const isVideo = !!ad.videoId;
  const key = ad.videoId ?? ad.imageIds?.[0];
  if (url) {
    return (
      <div className="relative h-12 w-12 shrink-0 overflow-hidden rounded-lg border border-border bg-surface-alt">
        <img src={url} alt="" loading="lazy" className="h-full w-full object-cover"
          onError={(e) => { (e.currentTarget as HTMLImageElement).style.visibility = 'hidden'; }} />
        {isVideo && (
          <span className="absolute inset-0 flex items-center justify-center bg-black/25">
            <Play size={13} className="text-white drop-shadow" />
          </span>
        )}
      </div>
    );
  }
  return (
    <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-lg border border-dashed border-border bg-surface-alt text-text-muted"
      title={key ? 'Preview unavailable' : 'No creative attached'}>
      {isVideo ? <Film size={15} /> : key ? <ImageIcon size={15} /> : <Megaphone size={15} />}
    </div>
  );
}

function AdRows({ ads, thumbs, metrics, currency, onChanged }: {
  ads: TiktokAd[]; thumbs: Record<string, string>; metrics: Map<string, TiktokInsightsRow>;
  currency?: string | null; onChanged: () => void;
}) {
  const [busy, setBusy] = useState<string | null>(null);

  const toggle = async (ad: TiktokAd) => {
    setBusy(ad.tiktokAdId);
    try {
      await tiktokService.setAdStatusV2(ad.tiktokAdId, ad.operationStatus === 'ENABLE' ? 'DISABLE' : 'ENABLE');
      onChanged();
    } catch (e) {
      if (!alreadyToasted(e)) toast.error(`TikTok refused — ${errMsg(e)}`);
    } finally { setBusy(null); }
  };

  if (ads.length === 0) return <div className="py-3 text-sm text-text-muted">No ads yet.</div>;

  return (
    <div>
      {ads.map((ad) => {
        const st = deliveryStatus(ad.operationStatus, ad.secondaryStatus);
        const key = ad.videoId ?? ad.imageIds?.[0];
        return (
          <div key={ad.tiktokAdId} className="flex items-center gap-3.5 border-t border-border/40 py-3 first:border-t-0">
            <AdThumb ad={ad} url={key ? thumbs[key] : undefined} />
            <div className="min-w-0 flex-1">
              <div className="truncate text-sm font-semibold text-text-primary">{ad.adName}</div>
              <div className="mt-0.5 truncate text-xs text-text-muted">
                {ad.adText?.trim() || [pretty(ad.adFormat), ad.callToAction ? pretty(ad.callToAction) : null].filter(Boolean).join(' · ') || 'No ad text'}
              </div>
              <div className="mt-0.5"><MetricsLine m={metrics.get(ad.tiktokAdId)} currency={currency} /></div>
            </div>
            <Pill {...st} />
            <Switch checked={ad.operationStatus === 'ENABLE'} disabled={busy === ad.tiktokAdId} label="Enable ad"
              onCheckedChange={() => toggle(ad)} />
          </div>
        );
      })}
    </div>
  );
}

// ── ad groups (second level) ─────────────────────────────────────────────

function AdGroupRows({ groups, adsByGroup, thumbs, metrics, adMetrics, currency, onChanged, onAddAd }: {
  groups: TiktokAdGroup[]; adsByGroup: Map<string, TiktokAd[]>; thumbs: Record<string, string>;
  metrics: Map<string, TiktokInsightsRow>; adMetrics: Map<string, TiktokInsightsRow>;
  currency?: string | null; onChanged: () => void; onAddAd: (adGroupId: string) => void;
}) {
  const [openId, setOpenId] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);

  const toggle = async (g: TiktokAdGroup) => {
    setBusy(g.tiktokAdGroupId);
    try {
      await tiktokService.setAdGroupStatus(g.tiktokAdGroupId, g.operationStatus === 'ENABLE' ? 'DISABLE' : 'ENABLE');
      onChanged();
    } catch (e) {
      if (!alreadyToasted(e)) toast.error(`TikTok refused — ${errMsg(e)}`);
    } finally { setBusy(null); }
  };

  if (groups.length === 0) {
    return <div className="py-2 text-sm text-text-muted">No ad groups yet — the campaign can't serve without one.</div>;
  }

  return (
    <div className="space-y-2">
      {groups.map((g) => {
        const open = openId === g.tiktokAdGroupId;
        const st = deliveryStatus(g.operationStatus, g.secondaryStatus);
        const ads = adsByGroup.get(g.tiktokAdGroupId) ?? [];
        return (
          <div key={g.tiktokAdGroupId}
            className={clsx('rounded-xl border transition-colors', open ? 'border-primary/60 bg-primary-soft/10 ring-1 ring-primary/15' : 'border-border/70 hover:border-border-strong')}>
            <div className="flex items-center gap-3 px-3.5 py-3">
              <button type="button" className="flex min-w-0 flex-1 items-center gap-3 text-left"
                onClick={() => setOpenId(open ? null : g.tiktokAdGroupId)}>
                {open ? <ChevronDown size={16} className="shrink-0 text-text-muted" />
                      : <ChevronRight size={16} className="shrink-0 text-text-muted" />}
                <Layers size={16} className="shrink-0 text-text-muted/70" />
                <div className="min-w-0">
                  <div className="truncate text-sm font-semibold text-text-primary">{g.adGroupName}</div>
                  <div className="truncate text-xs text-text-muted">
                    {fmtBudget(g.budgetMode, g.budget, currency)}
                    {g.optimizationGoal ? ` · ${pretty(g.optimizationGoal)}` : ''}
                    {` · ${plural(ads.length, 'ad')}`}
                  </div>
                  <div className="mt-0.5"><MetricsLine m={metrics.get(g.tiktokAdGroupId)} currency={currency} /></div>
                </div>
              </button>
              <Button variant="ghost" size="sm" className="h-8 gap-1 px-2.5 text-sm text-text-secondary hover:text-text-primary"
                onClick={() => onAddAd(g.tiktokAdGroupId)}>
                <Plus size={13} /> Ad
              </Button>
              <Pill {...st} />
              <Switch checked={g.operationStatus === 'ENABLE'} disabled={busy === g.tiktokAdGroupId} label="Enable ad group"
                onCheckedChange={() => toggle(g)} />
            </div>
            {open && (
              <div className="border-t border-border/50 px-3.5 pb-1 pl-12">
                <AdRows ads={ads} thumbs={thumbs} metrics={adMetrics} currency={currency} onChanged={onChanged} />
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

// ── identity banner ──────────────────────────────────────────────────────

function IdentityBar({ identities, loading, onRefresh }: { identities: Identity[] | null; loading: boolean; onRefresh: () => void }) {
  const navigate = useNavigate();
  const refreshBtn = (
    <button type="button" onClick={onRefresh} disabled={loading} title="Re-check linked accounts" aria-label="Refresh identities"
      className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border border-border text-text-muted transition-colors hover:bg-surface-alt hover:text-text-primary disabled:opacity-50">
      {loading ? <Spinner size={14} /> : <RefreshCw size={15} />}
    </button>
  );

  if (identities === null) return <div className="skeleton h-14 rounded-2xl" />;

  if (identities.length === 0) {
    return (
      <Card padded={false} className="flex flex-wrap items-center gap-x-4 gap-y-3 border-danger/25 px-4 py-3.5">
        <XCircle size={20} className="shrink-0 text-danger" />
        <div className="min-w-0 flex-1 text-sm">
          <span className="font-semibold text-text-primary">No TikTok account linked.</span>{' '}
          <span className="text-text-muted">
            Every ad must run under a real TikTok account — custom identities were retired. Link one to the advertiser in
            Business Center, then refresh.
          </span>
        </div>
        <div className="flex items-center gap-2">
          <Button size="sm" className="h-9" onClick={() => navigate('/tiktok/account')} iconLeft={<UserCircle2 size={14} />}>
            Link TikTok account
          </Button>
          {refreshBtn}
        </div>
      </Card>
    );
  }

  return (
    <div className="flex flex-wrap items-center gap-2">
      {identities.map((i) => (
        <span key={i.identityId} className="inline-flex items-center gap-2 rounded-full border border-border bg-surface px-3 py-1.5 text-sm text-text-primary shadow-soft">
          {i.profileImage
            ? <img src={i.profileImage} alt="" className="h-5 w-5 rounded-full object-cover" referrerPolicy="no-referrer" />
            : <UserCircle2 size={16} className="text-text-muted" />}
          <span className="max-w-[200px] truncate font-medium">{i.displayName || i.identityId}</span>
          <span className="text-2xs text-text-muted">{i.identityType === 'BC_AUTH_TT' ? 'Business Center' : 'Linked'}</span>
        </span>
      ))}
      <span className="flex-1" />
      {refreshBtn}
    </div>
  );
}

// ── tree ─────────────────────────────────────────────────────────────────

const VIEW_KEY = 'tt_tree_view';

export default function TiktokCampaignTree() {
  const navigate = useNavigate();
  const { currency: accountCurrency } = useTiktokAccountCurrency();

  const [campaigns, setCampaigns] = useState<TiktokCampaign[] | null>(null);
  const [adGroups, setAdGroups] = useState<TiktokAdGroup[]>([]);
  const [ads, setAds] = useState<TiktokAd[]>([]);
  const [listError, setListError] = useState<string | null>(null);
  const [refreshing, setRefreshing] = useState(false);
  const [openId, setOpenId] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [query, setQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [page, setPage] = useState(1);
  const [rangeDays, setRangeDays] = useState<RangeDays>(30);
  const [identities, setIdentities] = useState<Identity[] | null>(null);
  const [identitiesLoading, setIdentitiesLoading] = useState(false);
  const [thumbs, setThumbs] = useState<Record<string, string>>({});
  const [report, setReport] = useState<{
    campaign: Map<string, TiktokInsightsRow>; adgroup: Map<string, TiktokInsightsRow>; ad: Map<string, TiktokInsightsRow>;
    totals: { spend: number; impressions: number; clicks: number } | null;
  } | null>(null);
  const [view, setView] = useState<'list' | 'grid'>(() => {
    try { return (localStorage.getItem(VIEW_KEY) as 'list' | 'grid') || 'list'; } catch { return 'list'; }
  });
  const setViewPersisted = (v: 'list' | 'grid') => {
    setView(v);
    try { localStorage.setItem(VIEW_KEY, v); } catch { /* storage unavailable */ }
  };

  const load = useCallback(async () => {
    setListError(null);
    setRefreshing(true);
    try {
      const [cs, gs, as] = await Promise.all([
        tiktokService.listCampaigns(),
        tiktokService.listAdGroups(),
        tiktokService.listAds(),
      ]);
      setCampaigns(cs);
      setAdGroups(gs);
      setAds(as);
    } catch (e) {
      setListError(errMsg(e, 'Could not load campaigns.'));
      setCampaigns([]);
    } finally { setRefreshing(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const loadIdentities = useCallback(async () => {
    setIdentitiesLoading(true);
    try {
      const list = await tiktokService.listIdentities();
      // Custom identities can no longer run ads — only real linked accounts count.
      setIdentities(list.filter((i) => i.identityType !== 'CUSTOMIZED_USER'));
    } catch {
      setIdentities([]);
    } finally { setIdentitiesLoading(false); }
  }, []);
  useEffect(() => { loadIdentities(); }, [loadIdentities]);

  // Performance for the chosen window, best-effort — a report failure never hides the list.
  useEffect(() => {
    let alive = true;
    const { from, to } = reportWindow(rangeDays);
    const range = { from, to };
    (async () => {
      const [c, g, a, daily] = await Promise.all([
        tiktokService.aggregateReport({ level: 'campaign', ...range }).catch(() => [] as TiktokInsightsRow[]),
        tiktokService.aggregateReport({ level: 'adgroup', ...range }).catch(() => [] as TiktokInsightsRow[]),
        tiktokService.aggregateReport({ level: 'ad', ...range }).catch(() => [] as TiktokInsightsRow[]),
        tiktokService.dailyTotals({ level: 'campaign', ...range }).catch(() => null),
      ]);
      if (!alive) return;
      const toMap = (rows: TiktokInsightsRow[]) => new Map(rows.map((r) => [r._id, r]));
      const totals = daily && daily.length > 0
        ? daily.reduce((acc, d) => ({ spend: acc.spend + (d.spend || 0), impressions: acc.impressions + (d.impressions || 0), clicks: acc.clicks + (d.clicks || 0) }),
            { spend: 0, impressions: 0, clicks: 0 })
        : null;
      setReport({ campaign: toMap(c), adgroup: toMap(g), ad: toMap(a), totals });
    })();
    return () => { alive = false; };
  }, [rangeDays, campaigns]);

  // Ads only store asset ids; resolve covers once per distinct asset (see TiktokAds.tsx for the rationale).
  useEffect(() => {
    if (!openId) return;
    const visibleAds = ads.filter((a) => a.tiktokCampaignId === openId);
    if (visibleAds.length === 0) return;
    let cancelled = false;
    const wantedImages = new Set<string>();
    const wantedVideos = new Set<string>();
    visibleAds.forEach((a) => {
      const img = a.imageIds?.[0];
      if (img && !thumbs[img]) wantedImages.add(img);
      if (a.videoId && !thumbs[a.videoId]) wantedVideos.add(a.videoId);
    });
    if (wantedImages.size === 0 && wantedVideos.size === 0) return;
    (async () => {
      const found: Record<string, string> = {};
      if (wantedImages.size > 0) {
        const lib = await tiktokService.listImages({ pageSize: 200 }).catch(() => []);
        lib.forEach((i) => {
          if (wantedImages.has(i.imageId)) {
            const url = i.imagekitUrl ?? i.imageUrl;
            if (url) found[i.imageId] = url;
          }
        });
      }
      await Promise.all(Array.from(wantedVideos).map(async (vid) => {
        const r = await tiktokService.videoStatus(vid).catch(() => null);
        if (r?.coverUrl) found[vid] = r.coverUrl;
      }));
      if (!cancelled && Object.keys(found).length > 0) setThumbs((prev) => ({ ...prev, ...found }));
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ads, openId]);

  const currency = accountCurrency;
  const live = useMemo(() => (campaigns ?? []).filter((c) => !!c.tiktokCampaignId), [campaigns]);
  const filtered = useMemo(() => live.filter((c) =>
    (!query.trim() || `${c.campaignName} ${c.tiktokCampaignId}`.toLowerCase().includes(query.trim().toLowerCase())) &&
    (statusFilter === 'all' || (statusFilter === 'enabled' ? c.operationStatus === 'ENABLE' : c.operationStatus !== 'ENABLE')),
  ), [live, query, statusFilter]);
  const pageCount = Math.max(1, Math.ceil(filtered.length / 15));
  const currentPage = Math.min(page, pageCount);
  const visible = filtered.slice((currentPage - 1) * 15, currentPage * 15);
  const groupsByCampaign = useMemo(() => {
    const m = new Map<string, TiktokAdGroup[]>();
    adGroups.forEach((g) => { const l = m.get(g.tiktokCampaignId) ?? []; l.push(g); m.set(g.tiktokCampaignId, l); });
    return m;
  }, [adGroups]);
  const adsByGroup = useMemo(() => {
    const m = new Map<string, TiktokAd[]>();
    ads.forEach((a) => { const l = m.get(a.tiktokAdGroupId) ?? []; l.push(a); m.set(a.tiktokAdGroupId, l); });
    return m;
  }, [ads]);
  const adsByCampaign = useMemo(() => {
    const m = new Map<string, number>();
    ads.forEach((a) => m.set(a.tiktokCampaignId, (m.get(a.tiktokCampaignId) ?? 0) + 1));
    return m;
  }, [ads]);

  const activeCount = live.filter((c) => c.operationStatus === 'ENABLE').length;
  const tiles = [
    { label: 'Campaigns', value: String(live.length) },
    { label: 'Enabled', value: String(activeCount) },
    { label: 'Paused', value: String(live.length - activeCount) },
    { label: 'Spend', value: report?.totals ? fmtMoney(report.totals.spend, currency) : '—' },
    { label: 'Impressions', value: report?.totals ? fmtNum(report.totals.impressions) : '—' },
    { label: 'Clicks', value: report?.totals ? fmtNum(report.totals.clicks) : '—' },
  ];

  const toggle = async (c: TiktokCampaign) => {
    setBusy(c.tiktokCampaignId);
    try {
      await tiktokService.setCampaignStatus(c.tiktokCampaignId, c.operationStatus === 'ENABLE' ? 'DISABLE' : 'ENABLE');
      load();
    } catch (e) {
      if (!alreadyToasted(e)) toast.error(`TikTok refused — ${errMsg(e)}`);
    } finally { setBusy(null); }
  };

  // The Ad Groups / Ads tabs carry the create modals; scope them to the parent so "New" lands in the right place.
  const goAddAdGroup = (campaignId: string) => navigate(`/tiktok/campaigns?tab=adgroups&campaignId=${campaignId}`);
  const goAddAd = (adGroupId: string) => navigate(`/tiktok/campaigns?tab=ads&adGroupId=${adGroupId}`);

  const toolbar = (
    <div className="flex flex-wrap items-center gap-2">
      <label className="tt-tree-search"><Search size={16} /><input aria-label="Search campaigns" placeholder="Search by name or ID" value={query} onChange={(e) => { setQuery(e.target.value); setPage(1); }} /></label>
      <Select aria-label="Campaign status filter" value={statusFilter} onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }} className="h-9 rounded-lg border border-border bg-surface px-3 text-xs text-text-secondary"><option value="all">All statuses</option><option value="enabled">Enabled</option><option value="paused">Paused</option></Select>
      <div className="flex h-9 overflow-hidden rounded-lg border border-border text-xs font-medium">
        {([7, 30, 90] as RangeDays[]).map((d) => (
          <button key={d} type="button" onClick={() => setRangeDays(d)}
            className={clsx('px-3 transition-colors', rangeDays === d ? 'bg-surface-alt text-text-primary' : 'text-text-muted hover:bg-surface-alt/50')}>
            {d}d
          </button>
        ))}
      </div>
      <span className="flex-1" />
      <button type="button" onClick={() => load()} disabled={refreshing} title="Refresh" aria-label="Refresh"
        className="flex h-9 w-9 items-center justify-center rounded-lg border border-border text-text-muted transition-colors hover:bg-surface-alt hover:text-text-primary disabled:opacity-50">
        {refreshing ? <Spinner size={14} /> : <RefreshCw size={15} />}
      </button>
      <div className="flex h-9 overflow-hidden rounded-lg border border-border">
        <button type="button" onClick={() => setViewPersisted('list')} title="List view" aria-label="List view" aria-pressed={view === 'list'}
          className={clsx('px-2.5', view === 'list' ? 'bg-surface-alt text-text-primary' : 'text-text-muted hover:bg-surface-alt/50')}>
          <List size={15} />
        </button>
        <button type="button" onClick={() => setViewPersisted('grid')} title="Grid view" aria-label="Grid view" aria-pressed={view === 'grid'}
          className={clsx('px-2.5', view === 'grid' ? 'bg-surface-alt text-text-primary' : 'text-text-muted hover:bg-surface-alt/50')}>
          <LayoutGrid size={15} />
        </button>
      </div>

    </div>
  );

  const renderCampaignCard = (c: TiktokCampaign) => {
    const open = openId === c.tiktokCampaignId;
    const st = deliveryStatus(c.operationStatus, c.secondaryStatus);
    const groups = groupsByCampaign.get(c.tiktokCampaignId) ?? [];
    const adCount = adsByCampaign.get(c.tiktokCampaignId) ?? 0;
    return (
      <Card key={c.tiktokCampaignId} padded={false} className="overflow-hidden">
        <div className="tt-tree-row">
          <button type="button" className="flex min-w-0 flex-1 items-center gap-3 text-left"
            aria-expanded={open} onClick={() => setOpenId(open ? null : c.tiktokCampaignId)}>
            {open ? <ChevronDown size={18} className="shrink-0 text-text-muted" />
                  : <ChevronRight size={18} className="shrink-0 text-text-muted" />}
            <BrandTile />
            <div className="min-w-0">
              <div className="truncate text-[15px] font-semibold leading-tight text-text-primary">{c.campaignName}</div>
              <div className="mt-0.5 flex flex-wrap items-center gap-x-3 gap-y-0.5 text-sm text-text-muted">
                <span className="truncate">{pretty(c.objectiveType) || 'Campaign'} · {fmtBudget(c.budgetMode, c.budget, currency)}</span>
                <span className="inline-flex items-center gap-2.5 text-xs">
                  <span className="inline-flex items-center gap-1"><Layers size={12} /> {plural(groups.length, 'ad group')}</span>
                  <span className="inline-flex items-center gap-1"><FileText size={12} /> {plural(adCount, 'ad')}</span>
                </span>
              </div>
              <div className="mt-0.5"><MetricsLine m={report?.campaign.get(c.tiktokCampaignId)} currency={currency} /></div>
            </div>
          </button>
          <Pill {...st} />
          <Switch checked={c.operationStatus === 'ENABLE'} disabled={busy === c.tiktokCampaignId} label={`Enable ${c.campaignName}`}
            onCheckedChange={() => toggle(c)} />

        </div>
        {open && (
          <div className="tt-tree-children">
            <AdGroupRows groups={groups} adsByGroup={adsByGroup} thumbs={thumbs}
              metrics={report?.adgroup ?? new Map()} adMetrics={report?.ad ?? new Map()}
              currency={currency} onChanged={load} onAddAd={goAddAd} />
            <div className="pt-3">
              <Button variant="ghost" size="sm" className="h-8 gap-1.5 px-2.5 text-sm text-text-secondary hover:text-text-primary"
                onClick={() => goAddAdGroup(c.tiktokCampaignId)}>
                <Plus size={13} /> New ad group
              </Button>
            </div>
          </div>
        )}
      </Card>
    );
  };

  const renderGridCard = (c: TiktokCampaign) => {
    const st = deliveryStatus(c.operationStatus, c.secondaryStatus);
    const groups = groupsByCampaign.get(c.tiktokCampaignId) ?? [];
    const adCount = adsByCampaign.get(c.tiktokCampaignId) ?? 0;
    return (
      <Card key={c.tiktokCampaignId} padded={false} className="flex flex-col overflow-hidden">
        <div className="flex items-start justify-between gap-2 p-4 pb-0">
          <BrandTile />
          <Pill {...st} />
        </div>
        <div className="flex flex-1 flex-col p-4">
          <div className="truncate text-sm font-semibold text-text-primary">{c.campaignName}</div>
          <div className="mt-0.5 truncate text-xs text-text-muted">
            {pretty(c.objectiveType) || 'Campaign'} · {fmtBudget(c.budgetMode, c.budget, currency)}
          </div>
          <div className="mt-0.5 truncate text-xs text-text-muted">{plural(groups.length, 'ad group')} · {plural(adCount, 'ad')}</div>
          <div className="mt-2"><MetricsLine m={report?.campaign.get(c.tiktokCampaignId)} currency={currency} /></div>
          <div className="mt-3 flex items-center gap-2 border-t border-border/50 pt-3">
            <Switch checked={c.operationStatus === 'ENABLE'} disabled={busy === c.tiktokCampaignId} label={`Enable ${c.campaignName}`}
              onCheckedChange={() => toggle(c)} />
            <span className="text-xs text-text-muted">{c.operationStatus === 'ENABLE' ? 'Enabled' : 'Paused'}</span>
            <span className="flex-1" />
            <Button variant="ghost" size="sm" className="h-7 px-2 text-xs"
              onClick={() => { setViewPersisted('list'); setOpenId(c.tiktokCampaignId); }}>
              Open
            </Button>
          </div>
        </div>
      </Card>
    );
  };

  return (
    <div className="w-full space-y-4 px-7 pb-24 pt-4">
      <details className="rounded-xl border border-border bg-surface px-4 py-2.5"><summary className="cursor-pointer text-xs text-text-secondary">TikTok identities · {identitiesLoading ? 'loading…' : `${identities?.length ?? 0} linked`}</summary><div className="mt-2"><IdentityBar identities={identities} loading={identitiesLoading} onRefresh={loadIdentities} /></div></details>

      {toolbar}

      {campaigns === null && !listError ? (
        <div className="tt-tree-summary">
          {Array.from({ length: 6 }, (_, i) => <div key={i} className="skeleton h-[72px] rounded-2xl" />)}
        </div>
      ) : (
        <div className="tt-tree-summary">
          {tiles.map((t) => (
            <div key={t.label} className="min-w-0">
              <div className="text-xs text-text-muted">{t.label}</div>
              <div className="mt-1 truncate font-display text-lg font-semibold tabular-nums text-text-primary">{t.value}</div>
            </div>
          ))}
        </div>
      )}

      {listError && (
        <Card className="border-danger/30 bg-danger-soft p-4 text-sm text-danger">{listError}</Card>
      )}

      {campaigns === null && !listError && <SkeletonRows count={3} height="h-[76px]" />}

      {campaigns !== null && live.length === 0 && !listError && (
        <Card className="p-12 text-center">
          <span className="mx-auto mb-4 flex justify-center"><BrandTile size="lg" /></span>
          <p className="text-sm font-medium text-text-primary">No campaigns yet</p>
          <p className="mx-auto mt-1 max-w-xs text-xs text-text-muted">
            Build a campaign, ad group and ad in the Workspace, or sync anything already running in TikTok Ads Manager
            from the Campaigns tab.
          </p>
          <Button className="mt-5" onClick={() => navigate('/tiktok/workspace')} iconLeft={<Plus size={15} />}>
            New campaign
          </Button>
        </Card>
      )}

      {view === 'grid' && live.length > 0 && (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
          {visible.map(renderGridCard)}
        </div>
      )}

      {view === 'list' && live.length > 0 && (
        <div className="space-y-3">{visible.map(renderCampaignCard)}</div>
      )}
      {live.length > 0 && filtered.length === 0 && <div className="tt-panel text-center"><p className="text-sm">No campaigns match your filters.</p><Button size="sm" variant="ghost" onClick={() => { setQuery(''); setStatusFilter('all'); setPage(1); }}>Clear filters</Button></div>}
      {filtered.length > 0 && <div className="tt-pagination"><span>{(currentPage - 1) * 15 + 1}–{Math.min(currentPage * 15, filtered.length)} of {filtered.length} campaigns</span><div className="flex items-center gap-2"><Button size="sm" variant="secondary" disabled={currentPage === 1} onClick={() => setPage(currentPage - 1)}>Previous</Button><span>Page {currentPage} of {pageCount}</span><Button size="sm" variant="secondary" disabled={currentPage >= pageCount} onClick={() => setPage(currentPage + 1)}>Next</Button></div></div>}
    </div>
  );
}
